import { observable, action } from 'mobx'
import {
  type DocumentDetailsModalViewModel,
  type DocumentDetailsModalViewModelFactory,
} from '@/features/documents/DocumentDetailsModal/DocumentDetailsModalViewModel'
import { type Document } from '@/features/documents/Document'
import { Id } from '@/infra/id'

/**
 * Hosts the details modal of the document being viewed, keeping each
 * document's modal state for the rest of the session.
 */
export class DocumentDetailsRootViewModel {
  private readonly _documentDetailsVms = new Map<
    string,
    DocumentDetailsModalViewModel
  >()

  @observable
  public accessor selectedDocDetailsModalVm: DocumentDetailsModalViewModel | null =
    null

  constructor(
    private readonly _createDocumentDetailsModalViewModel: DocumentDetailsModalViewModelFactory,
  ) {
    this.viewDocumentDetails = this.viewDocumentDetails.bind(this)
  }

  /**
   * Shows the details of the document.
   */
  viewDocumentDetails(document: Document, viewAsAdmin?: boolean) {
    const docId = Id.assert(document.id)

    const existing = this._documentDetailsVms.get(docId)
    if (existing) {
      existing.setViewAsAdmin(viewAsAdmin ?? false)
      this._select(existing)
      return existing
    }

    const vm = this._createDocumentDetailsModalViewModel(document, viewAsAdmin)
    this._documentDetailsVms.set(docId, vm)
    this._select(vm)
    return vm
  }

  @action.bound
  private _select(vm: DocumentDetailsModalViewModel) {
    this.selectedDocDetailsModalVm?.close()
    this.selectedDocDetailsModalVm = vm
    vm.show()
  }
}
